import { useState, useEffect } from 'react'
import { useSearchVenditori, useCreateVenditore } from '@/hooks/useVenditori'
import { validateVenditore, hasErrors } from '@/utils/validation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Search, Plus, User, Phone, Check } from 'lucide-react'

/**
 * Componente per selezione venditore con ricerca e creazione rapida.
 * Simile a ClienteSelector, usato nel wizard ordini.
 * 
 * @param {Object} props
 * @param {Object} props.value - Venditore selezionato
 * @param {Function} props.onChange - Callback quando venditore cambia
 * @param {string} props.error - Messaggio di errore
 */
export default function VenditoreSelector({ value, onChange, error }) {
  const [searchTerm, setSearchTerm] = useState('')
  const [showResults, setShowResults] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newVenditoreData, setNewVenditoreData] = useState({
    nome: '',
    cognome: '',
    telefono: '',
  })
  const [formErrors, setFormErrors] = useState({})

  const { data: searchResults, isLoading } = useSearchVenditori(searchTerm)
  const createMutation = useCreateVenditore()

  const venditori = searchResults?.results || searchResults || []

  // Mostra risultati quando ci sono almeno 2 caratteri
  useEffect(() => {
    setShowResults(searchTerm.length >= 2)
  }, [searchTerm])

  const handleSelectVenditore = (venditore) => {
    onChange(venditore)
    setSearchTerm('')
    setShowResults(false)
  }

  const handleClearSelection = () => {
    onChange(null)
    setSearchTerm('')
  }

  const handleOpenCreateDialog = () => {
    // Pre-popola con il termine di ricerca
    const parts = searchTerm.trim().split(' ')
    setNewVenditoreData({
      nome: parts[0] || '',
      cognome: parts.slice(1).join(' ') || '',
      telefono: '',
    })
    setCreateDialogOpen(true)
  }

  const handleCreateVenditore = (e) => {
    e.preventDefault()
    
    const errors = validateVenditore(newVenditoreData)
    setFormErrors(errors)
    if (hasErrors(errors)) return
    
    createMutation.mutate(
      { ...newVenditoreData, attivo: true },
      {
        onSuccess: (response) => {
          // Seleziona il venditore appena creato
          onChange(response.data)
          setCreateDialogOpen(false)
          setSearchTerm('')
          setShowResults(false)
          setNewVenditoreData({ nome: '', cognome: '', telefono: '' })
          setFormErrors({})
        },
      }
    )
  }

  // Se c'è un venditore selezionato, mostra la card
  if (value) {
    return (
      <Card className="border-green-200 bg-green-50">
        <CardContent className="pt-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <User className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="font-medium">{value.nome_completo || `${value.nome} ${value.cognome}`}</p>
                {value.telefono && (
                  <p className="text-sm text-muted-foreground flex items-center gap-1">
                    <Phone className="h-3 w-3" />
                    {value.telefono}
                  </p>
                )}
              </div>
            </div>
            <Button variant="outline" size="sm" onClick={handleClearSelection}>
              Cambia
            </Button>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-2">
      {/* Campo di ricerca */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Cerca venditore per nome o cognome..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={`pl-10 ${error ? 'border-red-500' : ''}`}
        />
      </div>

      {error && <p className="text-sm text-red-500">{error}</p>}

      {/* Risultati ricerca */}
      {showResults && (
        <Card>
          <CardContent className="p-2">
            {isLoading ? (
              <div className="text-center py-4 text-muted-foreground">
                Ricerca in corso...
              </div>
            ) : venditori.length > 0 ? (
              <div className="space-y-1">
                {venditori.map((venditore) => (
                  <button
                    key={venditore.id}
                    type="button"
                    className="w-full text-left p-2 rounded hover:bg-accent transition-colors flex items-center gap-3"
                    onClick={() => handleSelectVenditore(venditore)}
                  >
                    <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center">
                      <User className="h-4 w-4" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">
                        {venditore.nome_completo || `${venditore.nome} ${venditore.cognome}`}
                      </p>
                      {venditore.telefono && (
                        <p className="text-sm text-muted-foreground">{venditore.telefono}</p>
                      )}
                    </div>
                    <Check className="h-4 w-4 text-muted-foreground" />
                  </button>
                ))}
              </div>
            ) : searchTerm.length >= 3 ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground mb-3">
                  Nessun venditore trovato per "{searchTerm}"
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleOpenCreateDialog}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Crea nuovo venditore
                </Button>
              </div>
            ) : (
              <div className="text-center py-4 text-muted-foreground">
                Digita almeno 3 caratteri per cercare o creare
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Dialog Creazione Rapida */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuovo Venditore</DialogTitle>
            <DialogDescription>
              Crea un nuovo venditore per associarlo all'ordine
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateVenditore}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="new-nome">Nome *</Label>
                <Input
                  id="new-nome"
                  value={newVenditoreData.nome}
                  onChange={(e) => {
                    setNewVenditoreData({ ...newVenditoreData, nome: e.target.value })
                    if (formErrors.nome) setFormErrors({ ...formErrors, nome: null })
                  }}
                  placeholder="Mario"
                  className={formErrors.nome ? 'border-destructive' : ''}
                />
                {formErrors.nome && (
                  <p className="text-sm text-destructive">{formErrors.nome}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-cognome">Cognome *</Label>
                <Input
                  id="new-cognome"
                  value={newVenditoreData.cognome}
                  onChange={(e) => {
                    setNewVenditoreData({ ...newVenditoreData, cognome: e.target.value })
                    if (formErrors.cognome) setFormErrors({ ...formErrors, cognome: null })
                  }}
                  placeholder="Rossi"
                  className={formErrors.cognome ? 'border-destructive' : ''}
                />
                {formErrors.cognome && (
                  <p className="text-sm text-destructive">{formErrors.cognome}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-telefono">Telefono</Label>
                <Input
                  id="new-telefono"
                  value={newVenditoreData.telefono}
                  onChange={(e) => {
                    setNewVenditoreData({ ...newVenditoreData, telefono: e.target.value })
                    if (formErrors.telefono) setFormErrors({ ...formErrors, telefono: null })
                  }}
                  placeholder="+39 333 1234567"
                  className={formErrors.telefono ? 'border-destructive' : ''}
                />
                {formErrors.telefono && (
                  <p className="text-sm text-destructive">{formErrors.telefono}</p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateDialogOpen(false)}
              >
                Annulla
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? 'Creazione...' : 'Crea e Seleziona'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
